import type { DialogueRefObject } from 'components/Dialogue/types';
import { LocalMessage } from 'hooks/business/conversation';
import { Dispatch, ProviderProps, ReactNode, SetStateAction, useContext, useMemo } from 'react';
import { TranslateMode } from 'services/api/overseaTranslate';
import createNamedContext from 'utils/createNamedContext';
import { UIMessages } from '../useChatView';
import { ContextMenuSingleType } from '../components/MessageMenu/ContextMenu';
import { ContextMenuParams, TriggerEvent } from 'react-contexify';
import { useCreation, useUnmount } from 'ahooks';
import { v4 as uuidV4 } from 'uuid';
import { useCleanSelectedState } from 'hooks/business/im/useMessageSelect';

export interface ChatContextValue {
  primaryKey: string;
  targetMessageExtra?: ReactNode;
  iframeMode?: boolean;
  galaxyModule?: Conversation.GalaxyModule;
  isGalaxy: boolean;
  dialogRef: DialogueRefObject | null;
  setDialogRef: (value: DialogueRefObject | null) => void;
  clickQuote: (msgId: string) => void;
  singleTransToSysLang: (
    msg: LocalMessage,
    lang?: General.Language,
    mode?: TranslateMode,
    translated?: boolean,
  ) => Promise<string[] | undefined>;
  showFeedback: (meta?: LocalMessage) => void;
  setContextMenuMessage: React.Dispatch<React.SetStateAction<UIMessages | undefined>>;
  setContextMenuSingleType: React.Dispatch<React.SetStateAction<ContextMenuSingleType | undefined>>;
  showContextMenu: (
    event: TriggerEvent,
    params?: Pick<ContextMenuParams, 'position' | 'id' | 'props'> | undefined,
  ) => void;
  videoTextState: [string | undefined, Dispatch<SetStateAction<string | undefined>>] | undefined;
  setVideoTextState: (
    value: [string | undefined, Dispatch<SetStateAction<string | undefined>>],
  ) => void;
  facebookBuyer?: Conversation.FacebookBuyer;
  highlightMsg?: Record<string, string>;
  /** 会话中是否出现过 QAPair 的标记 */
  hasQAPairMounted: boolean;
  setHasQAPairMounted: (value: boolean) => void;
  /** 是否隐藏消息列表的底部 */
  hideFooter?: boolean;
  showSourceContent?: boolean;
}

export const ChatContext = createNamedContext<ChatContextValue | null>('ChatContext', null);

export function useChatContext() {
  const value = useContext(ChatContext);

  if (value === null) {
    throw new Error(
      'useChatContext hook should be used in a context of ChatContextProvider component',
    );
  }

  return value;
}

export const ChatProvider = (
  props: ProviderProps<Omit<ChatContextValue, 'primaryKey'>> & { primaryKey?: string },
) => {
  const { children, value, primaryKey: primaryKeyProp } = props;

  const primaryKey = useCreation(() => primaryKeyProp || uuidV4(), []);

  const cleanSelectedState = useCleanSelectedState(primaryKey);

  useUnmount(() => {
    cleanSelectedState();
  });

  const contextValue = useMemo(() => {
    console.log('contextValue primaryKey', primaryKey);
    return { ...value, primaryKey };
  }, [value, primaryKey]);

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};
